import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  FormHelperText,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Checkbox,
  FormControlLabel,
  useTheme,
  useMediaQuery,
  Stack
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
  Send as SendIcon,
  AccountBalance as BankIcon,
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import ABOCard from './ABOCard';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import { cs } from 'date-fns/locale';

import { fetchABOFiles, generateABOFile, generateAndSubmitABOFile, getReadyInvoices } from '../../store/slices/aboSlice';
import { fetchInvoices } from '../../store/slices/invoiceSlice';
import { RootState, AppDispatch } from '../../store/store';
import api from '../../config/axios';
import { cs as translations } from '../../translations/cs';
import czechTranslations from '../../translations/czech';
import useModernAlert from '../../hooks/useModernAlert';
import { useBusinessUnits } from '../../hooks/useBusinessUnits';

const bankColors = {
  FIO_ABO: 'primary',
  FIO_API: 'primary',
  CSOB: 'secondary',
  KB: 'success',
  MONETA: 'warning',
  AIRBANK: 'info',
  RAIFFEISEN: 'default',
  UNICREDIT: 'primary',
  SPORITELNA: 'secondary'
} as const;

const bankLabels = {
  FIO_ABO: 'FIO ABO',
  FIO_API: 'FIO API',
  CSOB: 'ČSOB',
  KB: 'Komerční banka',
  MONETA: 'MONETA Money Bank',
  AIRBANK: 'Air Bank',
  RAIFFEISEN: 'Raiffeisenbank',
  UNICREDIT: 'UniCredit Bank',
  SPORITELNA: 'Česká spořitelna'
} as const;

const aboStatusColors = {
  generated: 'default',
  submitted: 'success',
  sent: 'success',
  sent_to_fio: 'primary',
  error: 'error'
} as const;

const aboStatusLabels = {
  generated: 'Vytvořeno',
  submitted: 'Odesláno',
  sent: 'Odesláno',
  sent_to_fio: 'Odesláno do FIO',
  error: 'Chyba'
} as const;

// Use translations for invoice status labels
const getInvoiceStatusLabel = (status: string) => {
  return czechTranslations.invoices.status[status as keyof typeof czechTranslations.invoices.status] || status;
};

const ABOFiles: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { showError, showSuccess, showWarning, showConfirm, AlertComponent } = useModernAlert();
  const { aboFiles, loading, error } = useSelector((state: RootState) => state.abo);
  const { invoices } = useSelector((state: RootState) => state.invoices);
  const { user } = useSelector((state: RootState) => state.auth);

  const [searchTerm, setSearchTerm] = useState('');
  const [bankFilter, setBankFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [businessUnitFilter, setBusinessUnitFilter] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState<Date | null>(null);
  const [dateTo, setDateTo] = useState<Date | null>(null);
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<string>('FIO_ABO');
  const [selectedInvoices, setSelectedInvoices] = useState<number[]>([]);
  const [readyInvoices, setReadyInvoices] = useState<any[]>([]);
  const [generating, setGenerating] = useState(false);
  const [bankAccountDialogOpen, setBankAccountDialogOpen] = useState(false);
  const [availableAccounts, setAvailableAccounts] = useState<any[]>([]);
  const [selectedABOFileId, setSelectedABOFileId] = useState<number | null>(null);
  const [selectedBankAccountId, setSelectedBankAccountId] = useState<string>('');
  const [sendingToFIO, setSendingToFIO] = useState(false);
  const [submitNow, setSubmitNow] = useState(false);
  const [immediateBankAccountId, setImmediateBankAccountId] = useState<number | null>(null);
  const [availableImmediateBankAccounts, setAvailableImmediateBankAccounts] = useState<any[]>([]);
  const [loadingImmediateBankAccounts, setLoadingImmediateBankAccounts] = useState(false);

  // Business units hook
  const { businessUnits, loading: businessUnitsLoading, error: businessUnitsError } = useBusinessUnits();

  // Define loadReadyInvoices function before it's used in hooks
  const loadReadyInvoices = useCallback(async () => {
    try {
      console.log('Loading ready invoices...');
      const params: any = {};
      if (businessUnitFilter !== 'all') {
        params.businessUnitId = businessUnitFilter;
      }
      const response = await api.get('/abo/invoices/ready', { params });
      console.log('Response:', response.data);
      setReadyInvoices(response.data.invoices || []);
      console.log('Ready invoices set:', response.data.invoices?.length || 0);
    } catch (error) {
      console.error('Error loading ready invoices:', error);
    }
  }, [businessUnitFilter]);

  // 🔄 Data refresh function
  const refreshData = useCallback(() => {
    const params: any = {};
    if (businessUnitFilter !== 'all') params.businessUnitId = businessUnitFilter;
    
    dispatch(fetchABOFiles(params));
    // Removed: dispatch(fetchInvoices({})); - not needed for ABO page
    // Removed: loadReadyInvoices(); - only called when opening Create ABO dialog
  }, [dispatch, businessUnitFilter]);

  useEffect(() => {
    refreshData();
  }, [refreshData]);

  // Reload ready invoices when business unit filter changes
  useEffect(() => {
    if (businessUnitFilter !== 'all') {
      loadReadyInvoices();
      setSelectedInvoices([]); // Clear selected invoices when business unit changes
    } else {
      setReadyInvoices([]); // Clear ready invoices when no business unit selected
      setSelectedInvoices([]);
    }
  }, [businessUnitFilter, loadReadyInvoices]);

  // 🔄 Refresh data when navigating back to ABO files
  useEffect(() => {
    if (location.pathname === '/abo') {
      refreshData();
    }
  }, [location.pathname, refreshData]);

  // 🎯 Scroll position preservation
  const [scrollPosition, setScrollPosition] = useState(0);
  
  // Save scroll position before refresh
  const saveScrollPosition = useCallback(() => {
    setScrollPosition(window.scrollY);
  }, []);

  // Restore scroll position after data update
  useEffect(() => {
    if (scrollPosition > 0) {
      window.scrollTo(0, scrollPosition);
    }
  }, [aboFiles, scrollPosition]);

  // Fetch bank accounts when submitNow changes
  useEffect(() => {
    if (submitNow && selectedOption === 'FIO_API' && businessUnitFilter !== 'all') {
      fetchImmediateBankAccounts();
    } else {
      setAvailableImmediateBankAccounts([]);
      setImmediateBankAccountId(null);
    }
  }, [submitNow, selectedOption, businessUnitFilter]);

  // Ensure businessUnits is always an array
  const safeBusinessUnits = Array.isArray(businessUnits) ? businessUnits : [];
  
  // Show loading state while business units are loading
  if (businessUnitsLoading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Načítání účetních jednotek...</Typography>
      </Box>
    );
  }
  
  // Show error state if business units failed to load
  if (businessUnitsError) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Chyba při načítání účetních jednotek: {businessUnitsError}
        </Alert>
        <Button onClick={() => window.location.reload()}>
          Obnovit stránku
        </Button>
      </Box>
    );
  }



  const fetchImmediateBankAccounts = async () => {
    setLoadingImmediateBankAccounts(true);
    try {
      const response = await api.get(`/abo/fio/bank-accounts?businessUnitId=${businessUnitFilter}`);
      const fioAccounts = response.data.bankAccounts.filter((account: any) => account.fio_token);
      setAvailableImmediateBankAccounts(fioAccounts);
      
      // Auto-select the first available account
      if (fioAccounts.length > 0) {
        setImmediateBankAccountId(fioAccounts[0].id);
      } else {
        setImmediateBankAccountId(null);
      }
    } catch (error) {
      console.error('Failed to fetch bank accounts:', error);
      showError('Nepodařilo se načíst bankovní účty');
    } finally {
      setLoadingImmediateBankAccounts(false);
    }
  };

  const handleGenerateABO = async () => {
    if (selectedInvoices.length === 0) return;

    // Validate bank account selection for immediate submission
    if (submitNow && selectedOption === 'FIO_API' && !immediateBankAccountId) {
      showError('Musíte vybrat bankovní účet pro okamžité odeslání');
      return;
    }

    setGenerating(true);
    try {
      if (submitNow && selectedOption === 'FIO_API') {
        // Use the new combined action with bank account selection
        await dispatch(generateAndSubmitABOFile({
          invoiceIds: selectedInvoices,
          bankType: selectedOption,
          businessUnitId: parseInt(businessUnitFilter),
          submitNow: true,
          bankAccountId: immediateBankAccountId!
        })).unwrap();
        
        showSuccess('Platební soubor byl vytvořen a odeslán do FIO banky');
      } else {
        // Use existing generation logic
        await dispatch(generateABOFile({
          invoiceIds: selectedInvoices,
          bankType: selectedOption,
          businessUnitId: parseInt(businessUnitFilter)
        })).unwrap();
        
        showSuccess('Platební soubor byl úspěšně vytvořen');
      }

      setGenerateDialogOpen(false);
      setSelectedInvoices([]);
      setSelectedOption('FIO_ABO');
      setSubmitNow(false);
      setImmediateBankAccountId(null);
      setAvailableImmediateBankAccounts([]);
      
      const params: any = {};
      if (businessUnitFilter !== 'all') params.businessUnitId = businessUnitFilter;
      dispatch(fetchABOFiles(params));
      dispatch(fetchInvoices({}));
    } catch (error: any) {
      console.error('Generation error:', error);
      
      // Handle structured error responses
      if (error.response?.data) {
        const errorData = error.response.data;
        
        if (errorData.type === 'validation_error') {
          // Show detailed validation error
          if (errorData.details && errorData.details.includes('Bank account validation failed')) {
            showError(`❌ ${errorData.error}\n\n${errorData.details}`);
          } else {
            showError(`❌ ${errorData.error}`);
          }
        } else if (errorData.type === 'fio_api_error') {
          // Show FIO API specific error
          showError(`❌ ${errorData.error}`);
        } else {
          // Show other structured errors
          showError(`❌ ${errorData.error}`);
        }
      } else {
        // Fallback to generic error
        showError(error.message || 'Chyba při vytváření platebního souboru');
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleCloseDialog = () => {
    setGenerateDialogOpen(false);
    setSelectedInvoices([]);
    setSelectedOption('FIO_ABO');
    setSubmitNow(false);
    setImmediateBankAccountId(null);
    setAvailableImmediateBankAccounts([]);
  };

  const handleInvoiceToggle = (invoiceId: number) => {
    setSelectedInvoices(prev => 
      prev.includes(invoiceId) 
        ? prev.filter(id => id !== invoiceId)
        : [...prev, invoiceId]
    );
  };

  const handleSelectAll = () => {
    if (selectedInvoices.length === readyInvoices.length) {
      setSelectedInvoices([]);
    } else {
      setSelectedInvoices(readyInvoices.map(invoice => invoice.id));
    }
  };

  const filteredABOFiles = aboFiles.filter((file: any) => {
    const matchesSearch = 
      file.filename?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      file.bankType?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesBank = bankFilter === 'all' || file.bankType === bankFilter;
    
    const matchesDate = (!dateFrom || new Date(file.created_at) >= dateFrom) &&
                       (!dateTo || new Date(file.created_at) <= dateTo);

    return matchesSearch && matchesBank && matchesDate;
  });

  const columns: GridColDef[] = [
    {
      field: 'filename',
      headerName: 'Název souboru',
      width: 250,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="bold">
          {params.value}
        </Typography>
      )
    },
    {
      field: 'bankType',
      headerName: 'Banka',
      width: 150,
      renderCell: (params) => (
        <Chip
          label={bankLabels[params.value as keyof typeof bankLabels] || params.value}
          color={bankColors[params.value as keyof typeof bankColors] || 'default'}
          size="small"
        />
      )
    },
    {
      field: 'invoiceCount',
      headerName: 'Počet faktur',
      width: 120,
      type: 'number'
    },
    {
      field: 'totalAmount',
      headerName: 'Celková částka',
      width: 150,
      valueGetter: (params) => `${params.value?.toLocaleString('cs-CZ')} Kč`
    },
    {
      field: 'createdAt',
      headerName: 'Vytvořeno',
      width: 150,
      valueGetter: (params) => {
        if (!params.value) return '';
        const date = new Date(params.value);
        return isNaN(date.getTime()) ? '' : format(date, 'dd.MM.yyyy HH:mm', { locale: cs });
      }
    },
    {
      field: 'createdBy',
      headerName: 'Vytvořil',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value || 'Neznámý'}
        </Typography>
      )
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 150,
      renderCell: (params) => (
        <Chip
          label={aboStatusLabels[params.value as keyof typeof aboStatusLabels] || params.value}
          color={aboStatusColors[params.value as keyof typeof aboStatusColors] || 'default'}
          size="small"
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Akce',
      width: 200,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            onClick={() => navigate(`/abo/${params.row.id}`)}
            title="Zobrazit detail"
          >
            <ViewIcon />
          </IconButton>
          {params.row.bankType === 'FIO_API' ? (
            // For FIO API files, show Send via API button instead of download
            params.row.status !== 'sent' ? (
              <IconButton
                size="small"
                onClick={() => handleSendToFIO(params.row.id)}
                title="Odeslat via API"
              >
                <SendIcon />
              </IconButton>
            ) : (
              <IconButton
                size="small"
                disabled
                title="Already sent"
              >
                <SendIcon />
              </IconButton>
            )
          ) : (
            // For other files, show download button (allow re-downloads)
            params.row.status !== 'sent' ? (
              <IconButton
                size="small"
                onClick={() => handleDownloadABO(params.row.id)}
                title="Stáhnout"
              >
                <DownloadIcon />
              </IconButton>
            ) : (
              <Chip
                label={aboStatusLabels[params.row.status as keyof typeof aboStatusLabels] || 'Odesláno'}
                color="success"
                size="small"
              />
            )
          )}
          <IconButton
            size="small"
            onClick={() => handleDeleteABO(params.row.id)}
            title="Smazat"
            color="error"
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      )
    }
  ];

  const handleDownloadABO = async (aboFileId: number) => {
    try {
      const response = await api.get(`/abo/${aboFileId}/download`, {
        responseType: 'blob'
      });

      // Get the filename from the response headers or use a default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `ABO_${aboFileId}.txt`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Create blob and download
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Download error:', error);
      showError(czechTranslations.alerts.downloadError);
    }
  };

  const handleSendToFIO = async (aboFileId: number) => {
    try {
      // First, get available FIO bank accounts
      const response = await api.post(`/abo/${aboFileId}/send-to-fio`, {});
      
      if (response.data.availableAccounts) {
        // Show bank account selection dialog
        setAvailableAccounts(response.data.availableAccounts);
        setSelectedABOFileId(aboFileId);
        setBankAccountDialogOpen(true);
      } else {
        // Direct response (shouldn't happen with current logic)
        showSuccess(response.data.message);
        const params: any = {};
        if (businessUnitFilter !== 'all') params.businessUnitId = businessUnitFilter;
        dispatch(fetchABOFiles(params));
        dispatch(fetchInvoices({}));
      }
    } catch (error: any) {
      console.error('Send error:', error);
      const errorMessage = error.response?.data?.error || 'Send failed';
      showError(czechTranslations.alerts.error(errorMessage));
    }
  };

  const handleConfirmSendToFIO = async () => {
    if (!selectedABOFileId || !selectedBankAccountId) return;

    setSendingToFIO(true);
    try {
      const response = await api.post(`/abo/${selectedABOFileId}/send-to-fio`, {
        bankAccountId: selectedBankAccountId
      });

      if (response.data) {
        const result = response.data;
        
        // Show detailed success message with past payment info - translate the FIO success message
        let successMessage;
        if (result.message.includes('Payments sent to FIO successfully')) {
          // Extract any additional info (like past payments note) from the original message
          const additionalInfo = result.message.replace('Payments sent to FIO successfully', '').trim();
          successMessage = `✅ ${czechTranslations.alerts.paymentsSentToFioSuccessfully}${additionalInfo}`;
        } else {
          successMessage = `✅ ${result.message}`;
        }
        if (result.pastPayments && result.pastPayments.length > 0) {
          successMessage += `\n\n⚠️ Poznámka: ${result.pastPayments.length} platba(y) s minulým datem splatnosti byla(y) upravena(y) na dnešní datum pro FIO API.`;
        }
        
        showSuccess(successMessage);
        setBankAccountDialogOpen(false);
        setSelectedBankAccountId('');
        setSelectedABOFileId(null);
        // Refresh the ABO files data
        const params: any = {};
        if (businessUnitFilter !== 'all') params.businessUnitId = businessUnitFilter;
        dispatch(fetchABOFiles(params));
        dispatch(fetchInvoices({}));
      }
    } catch (error: any) {
      console.error('Send error:', error);
      const errorMessage = error.response?.data?.error || 'Send failed';
      showError(czechTranslations.alerts.fioError(errorMessage));
    } finally {
      setSendingToFIO(false);
    }
  };

  const handleDeleteABO = async (aboFileId: number) => {
    showConfirm({
      title: '🗑️ Smazat ABO soubor',
      message: 'Opravdu chcete smazat tento ABO soubor? Tato akce nelze vrátit zpět.',
      type: 'warning',
      confirmText: 'Smazat',
      onConfirm: async () => {
        try {
          await api.delete(`/abo/${aboFileId}`);
          const params: any = {};
          if (businessUnitFilter !== 'all') params.businessUnitId = businessUnitFilter;
          dispatch(fetchABOFiles(params));
          dispatch(fetchInvoices({}));
          showSuccess('ABO soubor byl úspěšně smazán');
        } catch (error) {
          console.error('Delete error:', error);
          showError('Chyba při mazání ABO souboru');
        }
      }
    });
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2, sm: 0 } }}>
        <Typography variant="h4" component="h1">
          Platební prostředky
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, width: { xs: '100%', sm: 'auto' } }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              if (businessUnitFilter === 'all') {
                // Show error - this should be caught by disabled state, but just in case
                return;
              }
              // Load ready invoices when dialog opens
              loadReadyInvoices();
              setGenerateDialogOpen(true);
            }}
            disabled={businessUnitFilter === 'all'}
          >
            Vytvořit platební soubor
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={refreshData}
            disabled={loading}
          >
            Obnovit
          </Button>
        </Box>
      </Box>
      <Box>
        {businessUnitFilter === 'all' && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Vyberte účetní jednotku pro vytvoření platebního souboru
          </Typography>
        )}
      </Box>

      {/* Filters */}
      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 3 }}>
        <Grid container spacing={{ xs: 1, sm: 2 }} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Hledat"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size={isMobile ? "small" : "medium"}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size={isMobile ? "small" : "medium"}>
              <InputLabel>Účetní jednotka</InputLabel>
              <Select
                value={businessUnitFilter}
                onChange={(e) => setBusinessUnitFilter(e.target.value)}
                label="Účetní jednotka"
              >
                <MenuItem value="all">Všechny účetní jednotky</MenuItem>
                {safeBusinessUnits.map((businessUnit) => (
                  <MenuItem key={businessUnit.id} value={businessUnit.id}>
                    {businessUnit.name} ({businessUnit.ico})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size={isMobile ? "small" : "medium"}>
              <InputLabel>Banka</InputLabel>
              <Select
                value={bankFilter}
                onChange={(e) => setBankFilter(e.target.value)}
                label="Banka"
              >
                <MenuItem value="all">Všechny banky</MenuItem>
                {Object.entries(bankLabels).map(([key, label]) => (
                  <MenuItem key={key} value={key}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size={isMobile ? "small" : "medium"}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="all">Všechny statusy</MenuItem>
                {Object.entries(aboStatusLabels).map(([key, label]) => (
                  <MenuItem key={key} value={key}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Celkem ABO souborů
              </Typography>
              <Typography variant="h4">
                {aboFiles.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                FIO ABO
              </Typography>
              <Typography variant="h4" color="primary.main">
                {aboFiles.filter(f => f.bankType === 'FIO_ABO').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                FIO API
              </Typography>
              <Typography variant="h4" color="primary.main">
                {aboFiles.filter(f => f.bankType === 'FIO_API').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                CSOB
              </Typography>
              <Typography variant="h4" color="secondary.main">
                {aboFiles.filter(f => f.bankType === 'CSOB').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                KB
              </Typography>
              <Typography variant="h4" color="success.main">
                {aboFiles.filter(f => f.bankType === 'KB').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Ostatní banky
              </Typography>
              <Typography variant="h4" color="warning.main">
                {aboFiles.filter(f => !['FIO_ABO', 'FIO_API', 'CSOB', 'KB'].includes(f.bankType)).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Responsive Data Display */}
      {isMobile ? (
        /* Mobile Card Layout */
        <Box sx={{ 
          width: '100%',
          maxWidth: '100vw',
          overflow: 'hidden',
          boxSizing: 'border-box',
        }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : filteredABOFiles.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                Žádné ABO soubory nenalezeny
              </Typography>
            </Paper>
          ) : (
            <Box sx={{ mb: 3 }}>
              {filteredABOFiles.map((aboFile) => (
                <ABOCard
                  key={aboFile.id}
                  aboFile={aboFile}
                  onView={(id) => navigate(`/abo/${id}`)}
                  onDownload={(id) => handleDownloadABO(id)}
                  onSendToBank={(id) => {
                    setSelectedABOFileId(id);
                    setBankAccountDialogOpen(true);
                  }}
                />
              ))}
            </Box>
          )}
        </Box>
      ) : (
        /* Desktop DataGrid */
        <Paper sx={{ height: 600, width: '100%' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          ) : (
            <DataGrid
              rows={filteredABOFiles}
              columns={columns}
              pageSizeOptions={[10, 25, 50]}
              initialState={{
                pagination: {
                  paginationModel: { page: 0, pageSize: 25 },
                },
              }}
              disableRowSelectionOnClick
              getRowId={(row) => row.id}
            />
          )}
        </Paper>
      )}

      {/* Generate ABO Dialog */}
      <Dialog open={generateDialogOpen} onClose={() => setGenerateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Vytvořit nový platební soubor</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Účetní jednotka</InputLabel>
                <Select
                  value={businessUnitFilter}
                  onChange={(e) => setBusinessUnitFilter(e.target.value)}
                  label="Účetní jednotka"
                  error={businessUnitFilter === 'all'}
                >
                  <MenuItem value="all" disabled>Vyberte účetní jednotku</MenuItem>
                  {safeBusinessUnits.map((businessUnit) => (
                    <MenuItem key={businessUnit.id} value={businessUnit.id}>
                      {businessUnit.name} ({businessUnit.ico})
                    </MenuItem>
                  ))}
                </Select>

              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Banka a způsob odeslání</InputLabel>
                <Select
                  value={selectedOption}
                  onChange={(e) => setSelectedOption(e.target.value)}
                  label="Banka a způsob odeslání"
                >
                  <MenuItem value="FIO_ABO">FIO Banka - ABO soubor</MenuItem>
                  <MenuItem value="FIO_API">FIO Banka - API (přímé odeslání)</MenuItem>
                  <MenuItem value="CSOB">ČSOB - ABO soubor</MenuItem>
                  <MenuItem value="KB">Komerční banka - ABO soubor</MenuItem>
                  <MenuItem value="MONETA">MONETA Money Bank - ABO soubor</MenuItem>
                  <MenuItem value="AIRBANK">Air Bank - ABO soubor</MenuItem>
  
                  <MenuItem value="RAIFFEISEN">Raiffeisenbank - ABO soubor</MenuItem>
                  <MenuItem value="UNICREDIT">UniCredit Bank - ABO soubor</MenuItem>
                  <MenuItem value="SPORITELNA">Česká spořitelna - ABO soubor</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {/* Submit now option - only show for FIO API */}
            {selectedOption === 'FIO_API' && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={submitNow}
                      onChange={(e) => setSubmitNow(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Odeslat ihned do FIO banky"
                />
              </Grid>
            )}
            
            {/* Bank account selection - only show when submitNow is true */}
            {submitNow && selectedOption === 'FIO_API' && (
              <Grid item xs={12}>
                {loadingImmediateBankAccounts ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={20} />
                    <Typography>Načítání bankovních účtů...</Typography>
                  </Box>
                ) : availableImmediateBankAccounts.length > 0 ? (
                  <FormControl fullWidth>
                    <InputLabel>Bankovní účet *</InputLabel>
                    <Select
                      value={immediateBankAccountId || ''}
                      onChange={(e) => setImmediateBankAccountId(e.target.value as number)}
                      label="Bankovní účet *"
                      required
                    >
                      {availableImmediateBankAccounts.map((account: any) => (
                        <MenuItem key={account.id} value={account.id}>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {account.account_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {account.account_number} / {account.bank_code}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      Vyberte účet, ze kterého budou provedeny platby
                    </FormHelperText>
                  </FormControl>
                ) : (
                  <Alert severity="warning">
                    Pro tuto firmu nejsou nakonfigurovány žádné FIO bankovní účty s API tokenem.
                    <br />
                    <Button 
                      size="small" 
                      sx={{ mt: 1 }}
                      onClick={() => {
                        // Navigate to business units to configure bank accounts
                        navigate('/business-units');
                        handleCloseDialog();
                      }}
                    >
                      Nakonfigurovat bankovní účty
                    </Button>
                  </Alert>
                )}
              </Grid>
            )}
            
            {/* Warning about immediate submission */}
            {submitNow && selectedOption === 'FIO_API' && availableImmediateBankAccounts.length > 0 && (
              <Grid item xs={12}>
                <Alert severity="info">
                  <Typography variant="body2" fontWeight="medium">
                    ⚠️ Okamžité odeslání do FIO banky
                  </Typography>
                  <Typography variant="body2">
                    Platby budou odeslány do FIO banky ihned po vytvoření souboru. 
                    Ujistěte se, že máte dostatek prostředků na účtu {immediateBankAccountId ? 
                      availableImmediateBankAccounts.find(acc => acc.id === immediateBankAccountId)?.account_number : ''
                    }.
                  </Typography>
                </Alert>
              </Grid>
            )}
            
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Vyberte faktury pro platební soubor
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedInvoices.length === readyInvoices.length && readyInvoices.length > 0}
                      indeterminate={selectedInvoices.length > 0 && selectedInvoices.length < readyInvoices.length}
                      onChange={handleSelectAll}
                    />
                  }
                  label="Vybrat všechny"
                />
                <Typography variant="body2" sx={{ ml: 2 }}>
                  Vybráno: {selectedInvoices.length} z {readyInvoices.length}
                </Typography>
              </Box>
              <Paper sx={{ maxHeight: 400, overflow: 'auto' }}>
                <List>
                  {readyInvoices.map((invoice) => (
                    <ListItem key={invoice.id} dense>
                      <Checkbox
                        checked={selectedInvoices.includes(invoice.id)}
                        onChange={() => handleInvoiceToggle(invoice.id)}
                      />
                      <ListItemText
                        primary={invoice.invoice_number}
                        secondary={`${invoice.supplier_name} - ${invoice.total_amount?.toLocaleString('cs-CZ')} Kč`}
                      />
                      <ListItemSecondaryAction>
                        <Chip
                          label={getInvoiceStatusLabel(invoice.status)}
                          size="small"
                          color="primary"
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>
          </Grid>
          {generating && (
            <Box sx={{ width: '100%', mt: 2 }}>
              <LinearProgress />
              <Typography variant="body2" sx={{ mt: 1 }}>
                Vytvářím ABO soubor...
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={generating}>
            Zrušit
          </Button>
          <Button
            onClick={handleGenerateABO}
            disabled={
              selectedInvoices.length === 0 || 
              generating || 
              (submitNow && selectedOption === 'FIO_API' && !immediateBankAccountId) ||
              (submitNow && selectedOption === 'FIO_API' && availableImmediateBankAccounts.length === 0)
            }
            variant="contained"
          >
            {submitNow && selectedOption === 'FIO_API' 
              ? 'Vytvořit a odeslat' 
              : 'Vytvořit platební soubor'
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bank Account Selection Dialog */}
      <Dialog open={bankAccountDialogOpen} onClose={() => setBankAccountDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Vyberte bankovní účet pro odeslání</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Vyberte bankovní účet, ze kterého chcete odeslat platby:
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Bankovní účet</InputLabel>
            <Select
              value={selectedBankAccountId}
              onChange={(e) => setSelectedBankAccountId(e.target.value)}
              label="Bankovní účet"
            >
              {availableAccounts.map((account) => (
                <MenuItem key={account.id} value={account.id}>
                  {account.accountName} - {account.accountNumber}/{account.bankCode}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBankAccountDialogOpen(false)} disabled={sendingToFIO}>
            Zrušit
          </Button>
          <Button
            onClick={handleConfirmSendToFIO}
            disabled={!selectedBankAccountId || sendingToFIO}
            variant="contained"
          >
            {sendingToFIO ? 'Odesílám...' : 'Odeslat via FIO API'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {AlertComponent}
    </Box>
  );
};

export default ABOFiles; 